import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Package, Plus, X, TriangleAlert } from "lucide-react";

export default function InventoryBalancesPage() {
  const { branchId, hasPerm } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [only, setOnly] = useState("all");
  const [receiving, setReceiving] = useState(null); // {item, quantity, cost, notes}
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!branchId) return;
    try {
      const { data } = await api.get("/inventory/balances", { params: { branch_id: branchId } });
      setRows(data);
    } catch (_) {}
    finally { setLoading(false); }
  };
  useEffect(() => { setLoading(true); load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [branchId]);

  const filtered = useMemo(() => {
    let out = rows;
    if (only === "low") out = out.filter((r) => r.low_stock);
    else if (only === "zero") out = out.filter((r) => r.balance_base <= 0);
    if (q) {
      const s = q.toLowerCase();
      out = out.filter((r) => r.name.toLowerCase().includes(s) || r.code.toLowerCase().includes(s));
    }
    return out;
  }, [rows, only, q]);

  const totalValue = filtered.reduce((sum, r) => sum + (r.value || 0), 0);

  const receive = async () => {
    if (!receiving || !receiving.quantity) return;
    try {
      await api.post("/inventory/movements", {
        branch_id: branchId,
        item_id: receiving.item.item_id,
        type: "receive",
        quantity: parseFloat(receiving.quantity),
        as_purchase_unit: true,
        unit_cost: receiving.cost ? parseFloat(receiving.cost) : null,
        notes: receiving.notes || "",
      });
      toast.success(`Received ${receiving.quantity} ${receiving.item.purchase_unit} of ${receiving.item.name}`);
      setReceiving(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="inventory-balances-page">
      <div className="flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="section-eyebrow ink-subtle">Inventory · Live balances</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">On-hand stock</h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">
            Computed from the movements ledger. Low-stock items are surfaced first. Click any row to receive a shipment.
          </p>
        </div>
        <div className="text-right">
          <div className="section-eyebrow">Filtered value</div>
          <div className="mt-3 display-heading text-4xl mono ink" data-testid="inv-bal-total-value">{peso(totalValue)}</div>
          <div className="mt-1 tiny-label ink-subtle">{filtered.length} of {rows.length} items shown</div>
        </div>
      </div>

      {!branchId && (
        <div className="mt-14 border hairline-soft rounded-md p-8 text-center ink-subtle text-sm">
          Select a branch first — balances are per-branch.
        </div>
      )}

      {branchId && (
        <>
          <div className="mt-10 flex flex-wrap items-center gap-3 animate-fade-up animate-fade-up-1">
            <input placeholder="Search item or code…" value={q} onChange={(e) => setQ(e.target.value)}
                   data-testid="inv-bal-search"
                   className="h-10 rounded-md border hairline surface-card px-3 text-sm ink w-72 focus:border-foreground/50 outline-none"/>
            <div className="flex items-center gap-1 border hairline-soft rounded-full p-1">
              {[
                { k: "all", label: "All" },
                { k: "low", label: "Low-stock" },
                { k: "zero", label: "Out" },
              ].map((f) => (
                <button key={f.k} onClick={() => setOnly(f.k)}
                        data-testid={`inv-bal-filter-${f.k}`}
                        className={`h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.24em] font-bold transition-all ${
                          only === f.k ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"
                        }`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {loading && <div className="mt-8 ink-subtle text-sm">Loading balances…</div>}

          {!loading && (
            <div className="mt-8 border-y hairline-soft divide-y hairline-soft animate-fade-up animate-fade-up-2" data-testid="inv-bal-table">
              <div className="grid grid-cols-12 py-3 tiny-label ink-subtle">
                <div className="col-span-4">Item</div>
                <div className="col-span-2">Category</div>
                <div className="col-span-2 text-right">Balance</div>
                <div className="col-span-2 text-right">In purchase units</div>
                <div className="col-span-2 text-right">Value</div>
              </div>
              {filtered.map((r) => (
                <button key={r.item_id}
                        onClick={() => hasPerm("inventory.adjust") && setReceiving({ item: r, quantity: "", cost: r.current_cost, notes: "" })}
                        disabled={!hasPerm("inventory.adjust")}
                        data-testid={`inv-bal-row-${r.item_id}`}
                        className={`w-full text-left grid grid-cols-12 py-3 items-center transition-colors ${
                          hasPerm("inventory.adjust") ? "hover:bg-foreground/[0.03] cursor-pointer" : "cursor-default"
                        }`}>
                  <div className="col-span-4 pr-4 flex items-center gap-2">
                    {r.low_stock && <TriangleAlert size={13} className="text-red-500 shrink-0"/>}
                    <div className="min-w-0">
                      <div className="text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{r.name}</div>
                      <div className="text-xs ink-subtle mono mt-0.5">{r.code}</div>
                    </div>
                  </div>
                  <div className="col-span-2 tiny-label">{r.category.replace("_"," ")}</div>
                  <div className={`col-span-2 text-right mono text-sm ${r.balance_base <= 0 ? "text-red-500" : r.low_stock ? "text-amber-500" : "ink"}`}>
                    {r.balance_base} {r.base_unit}
                  </div>
                  <div className="col-span-2 text-right mono text-sm ink-muted">
                    ≈ {r.balance_purchase} {r.purchase_unit}
                  </div>
                  <div className="col-span-2 text-right mono text-sm ink font-semibold">{peso(r.value)}</div>
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="py-8 ink-subtle text-center text-sm">No items match the filter.</div>
              )}
            </div>
          )}
        </>
      )}

      {/* Receive drawer */}
      {receiving && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="inv-receive-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => setReceiving(null)}/>
          <div className="relative w-full max-w-md surface-2 border-l hairline h-full p-6 overflow-y-auto shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <div className="section-eyebrow">Inventory · Receive</div>
                <h2 className="mt-2 display-heading text-2xl ink">{receiving.item.name}</h2>
                <div className="mt-2 tiny-label ink-subtle">Current: <span className="mono ink">{receiving.item.balance_base} {receiving.item.base_unit}</span></div>
              </div>
              <button onClick={() => setReceiving(null)} className="tiny-label ink-subtle hover:ink"><X size={16}/></button>
            </div>

            <label className="block mt-6">
              <div className="tiny-label">Quantity ({receiving.item.purchase_unit})</div>
              <input autoFocus type="number" step="0.01"
                     value={receiving.quantity}
                     onChange={(e) => setReceiving({ ...receiving, quantity: e.target.value })}
                     data-testid="inv-receive-qty"
                     className="mt-2 h-14 w-full rounded-md border hairline surface-card px-4 mono ink text-2xl font-semibold focus:border-foreground/50 outline-none"
                     placeholder="0"/>
              {receiving.quantity && (
                <div className="mt-2 tiny-label ink-subtle">
                  = <span className="mono ink">{(parseFloat(receiving.quantity) || 0) * receiving.item.purchase_to_base_ratio} {receiving.item.base_unit}</span>
                </div>
              )}
            </label>

            <label className="block mt-4">
              <div className="tiny-label">Unit cost per {receiving.item.base_unit} (optional)</div>
              <input type="number" step="0.01"
                     value={receiving.cost}
                     onChange={(e) => setReceiving({ ...receiving, cost: e.target.value })}
                     data-testid="inv-receive-cost"
                     className="mt-2 h-11 w-full rounded-md border hairline surface-card px-3 mono ink text-sm"
                     placeholder="0.00"/>
              <div className="mt-1 tiny-label ink-subtle">Updates the item's current cost when saved.</div>
            </label>

            <label className="block mt-4">
              <div className="tiny-label">Notes</div>
              <input value={receiving.notes} onChange={(e) => setReceiving({ ...receiving, notes: e.target.value })}
                     data-testid="inv-receive-notes"
                     className="mt-2 h-11 w-full rounded-md border hairline surface-card px-3 ink text-sm"
                     placeholder="Supplier · PO # · etc."/>
            </label>

            <div className="mt-6 flex gap-2">
              <button onClick={() => setReceiving(null)} className="btn-pill btn-pill-outline h-11 flex-1">Cancel</button>
              <button onClick={receive} disabled={!receiving.quantity} data-testid="inv-receive-submit" className="btn-pill btn-pill-solid h-11 flex-1 disabled:opacity-50">
                <Plus size={13}/> Receive stock
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
